'use client';

/**
 * Mounts the singleton perf reporter on first client render and reports
 * navigation timings on App Router pathname changes.
 *
 * Designed to be inert at SSR — every browser API touch is gated by
 * ``typeof window``. Failure to capture a metric never breaks the page.
 */

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import { getPerfReporter } from '@/lib/perf-reporter';

export function PerfReporter() {
  const pathname = usePathname();

  useEffect(() => {
    getPerfReporter().start();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // App Router doesn't emit a fresh PerformanceNavigationTiming on
    // soft navigations, so we synthesise a lightweight "soft_nav" pulse
    // by re-reading the most recent nav entry and tagging it with the
    // new pathname.
    const nav = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming | undefined;
    if (!nav) return;
    getPerfReporter().add({
      kind: 'navigation',
      path: pathname,
      ts: Date.now(),
      navigation: {
        type: 'navigate',
        ttfb_ms: nav.responseStart,
        dom_content_loaded_ms: nav.domContentLoadedEventEnd,
        dom_interactive_ms: nav.domInteractive,
        load_event_end_ms: nav.loadEventEnd,
        transfer_size_bytes: nav.transferSize,
      },
    });
  }, [pathname]);

  return null;
}
